import React, { useRef, useState, useEffect } from 'react';
import { Box, ButtonGroup, Button, Typography, IconButton } from '@mui/material';
import { alpha, SxProps, Theme } from '@mui/material/styles';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import DayMap from '../DayMap';
import { PlannerSection } from '../../types/planner';

interface PlannerMapPanelProps {
  sections: PlannerSection[];
  activeDayIndex: number;
  onDayChange: (index: number) => void;
  travelData: any | null;
  onActivitySelect?: (activityId: string) => void;
  restaurants?: any[]; // Pass restaurants with selection state
  mapContainerSx?: SxProps<Theme>;
  stickyOffset?: number;
  headerTransparent?: boolean;
  isFullscreen?: boolean;
  onFullscreenChange?: (isFullscreen: boolean) => void;
}

export const PlannerMapPanel: React.FC<PlannerMapPanelProps> = ({
  sections,
  activeDayIndex,
  onDayChange,
  travelData,
  onActivitySelect,
  restaurants: restaurantsProp,
  mapContainerSx,
  stickyOffset = 72,
  headerTransparent = false,
  isFullscreen,
  onFullscreenChange,
}) => {
  const detailedDayCount = travelData?.travel_plan?.length ?? 0;
  const detailedSections = detailedDayCount > 0 ? sections.slice(0, detailedDayCount) : sections;
  const restaurantsWithSelection = restaurantsProp || [];
  const buttonGroupRef = useRef<HTMLDivElement>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);
  const isFullscreenControlled = typeof isFullscreen === 'boolean';
  const [internalFullscreen, setInternalFullscreen] = React.useState(false);
  const fullscreen = isFullscreenControlled ? Boolean(isFullscreen) : internalFullscreen;
  const handleFullscreenChange = React.useCallback((next: boolean) => {
    if (!isFullscreenControlled) {
      setInternalFullscreen(next);
    }
    if (onFullscreenChange) {
      onFullscreenChange(next);
    }
  }, [isFullscreenControlled, onFullscreenChange]);
  
  // Check scroll position to show/hide arrows
  useEffect(() => {
    const checkScroll = () => {
      if (buttonGroupRef.current) {
        const { scrollLeft, scrollWidth, clientWidth } = buttonGroupRef.current;
        setShowLeftArrow(scrollLeft > 5); // Small threshold to avoid flickering
        setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 5);
      }
    };
    
    // Check immediately and after a short delay to ensure DOM is ready
    checkScroll();
    const timeoutId = setTimeout(checkScroll, 100);
    const interval = setInterval(checkScroll, 200);
    
    // Also listen to scroll events
    const element = buttonGroupRef.current;
    if (element) {
      element.addEventListener('scroll', checkScroll);
    }
    
    // Use ResizeObserver to detect container size changes
    let resizeObserver: ResizeObserver | null = null;
    if (element && typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(checkScroll);
      resizeObserver.observe(element);
    }
    
    return () => {
      clearTimeout(timeoutId);
      clearInterval(interval);
      if (element) {
        element.removeEventListener('scroll', checkScroll);
      }
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [detailedSections.length, activeDayIndex]);
  
  // Scroll to active day button
  useEffect(() => {
    if (buttonGroupRef.current && activeDayIndex >= 0 && activeDayIndex < detailedSections.length) {
      const buttons = buttonGroupRef.current.querySelectorAll('button');
      const activeButton = buttons[activeDayIndex + 1]; // +1 because "All Days" is first
      if (activeButton) {
        activeButton.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
    }
  }, [activeDayIndex, detailedSections.length]);
  
  const scrollLeft = () => {
    if (buttonGroupRef.current) {
      buttonGroupRef.current.scrollBy({ left: -200, behavior: 'smooth' });
    }
  };
  
  const scrollRight = () => {
    if (buttonGroupRef.current) {
      buttonGroupRef.current.scrollBy({ left: 200, behavior: 'smooth' });
    }
  };

  if (!travelData || !detailedSections.length) {
    return (
      <Box sx={(theme) => ({ p: 3, borderRadius: 4, border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`, backgroundColor: theme.palette.background.paper })}>
        <Typography variant="h6">Map preview</Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1.5 }}>
          Generate a trip to explore the map view of your curated activities.
        </Typography>
      </Box>
    );
  }

  const hasDetailedActivities = Array.isArray(travelData.activities) && travelData.activities.length > 0;
  const hasRestaurants = restaurantsWithSelection.length > 0;

  if (!hasDetailedActivities && !hasRestaurants) {
    return (
      <Box sx={(theme) => ({ p: 3, borderRadius: 4, border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`, backgroundColor: theme.palette.background.paper })}>
        <Typography variant="h6">Map preview</Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1.5 }}>
          We will add pins here as soon as full activity details arrive.
        </Typography>
      </Box>
    );
  }

  // Create activities lookup
  const activitiesLookup = new Map<string, any>();
  (travelData.activities || []).forEach((activity: any) => {
    activitiesLookup.set(activity.id, activity);
  });

  // Create restaurants lookup from prop (which has selection state)
  const restaurantsLookup = new Map<string, any>();
  restaurantsWithSelection.forEach((restaurant: any) => {
    restaurantsLookup.set(restaurant.id, restaurant);
  });

  // Determine which activities to show
  let mapActivities: any[];
  
  // Use -1 for "All Days" mode, -2 for "Restaurants" mode, -3 for "Overflow" mode
  const isAllDaysMode = activeDayIndex === -1;
  const isRestaurantsMode = activeDayIndex === -2;
  const isOverflowMode = activeDayIndex === -3;
  
  // Check if there are overflow activities
  const overflowSection = sections.find(section => section.id === 'day-overflow');
  const hasOverflowActivities = (overflowSection && overflowSection.experiences.length > 0) || 
                                 (travelData.overflow_activities && travelData.overflow_activities.length > 0);
  
  // Get selected status from sections and restaurants
  const getActivitySelectedStatus = (activityId: string): boolean => {
    for (const section of sections) {
      const experience = section.experiences.find((exp) => exp.id === activityId);
      if (experience) {
        return experience.isSelected ?? true;
      }
    }
    return true; // Default to selected if not found
  };

  if (isOverflowMode) {
    // Show only overflow activities (Days 5-8, etc.)
    mapActivities = [];
    
    // Get overflow activities from sections
    if (overflowSection) {
      overflowSection.experiences.forEach((experience) => {
        const isSelected = experience.isSelected !== false;
        if (isSelected) {
          const fullActivity = activitiesLookup.get(experience.id);
          const experienceData = experience.extraDetails as any;
          
          // Calculate proper dayIndex from day_number (0-indexed: day 5 = index 4, day 6 = index 5, etc.)
          const dayNumber = experienceData?.day_number;
          const overflowDayIndex = typeof dayNumber === 'number' && dayNumber > 0 
            ? dayNumber - 1  // Convert 1-based day_number to 0-based dayIndex
            : detailedDayCount; // Fallback to first overflow day index
          
          const mergedActivity = {
            ...(fullActivity || {}),
            ...(experienceData || {}),
            id: experience.id,
            name: experience.name || fullActivity?.name || experienceData?.name,
            description: experience.description || fullActivity?.description || experienceData?.description,
            address: experience.address || fullActivity?.address || experienceData?.address,
            location_name: experienceData?.location_name || fullActivity?.location_name,
            latitude: experienceData?.latitude || fullActivity?.latitude,
            longitude: experienceData?.longitude || fullActivity?.longitude,
            duration: experience.duration || fullActivity?.duration || experienceData?.duration,
            cost: experience.cost || fullActivity?.cost || experienceData?.cost,
            working_hours: experience.workingHours || fullActivity?.working_hours || experienceData?.working_hours,
            category: experience.category || fullActivity?.category || experienceData?.category,
            time_of_visit: experience.timeOfVisit || fullActivity?.time_of_visit || experienceData?.time_of_visit,
          };
          
          mapActivities.push({
            id: experience.id,
            name: experience.name,
            fullActivity: mergedActivity,
            dayIndex: overflowDayIndex, // Use proper dayIndex for color assignment
            isRestaurant: false,
          });
        }
      });
    }
    
    // Also check travelData.overflow_activities directly
    if (travelData.overflow_activities && travelData.overflow_activities.length > 0) {
      travelData.overflow_activities.forEach((overflowGroup: any) => {
        const groupDayNumber = overflowGroup.day_number;
        // Calculate proper dayIndex from day_number (0-indexed: day 5 = index 4, day 6 = index 5, etc.)
        const overflowDayIndex = typeof groupDayNumber === 'number' && groupDayNumber > 0 
          ? groupDayNumber - 1  // Convert 1-based day_number to 0-based dayIndex
          : detailedDayCount; // Fallback to first overflow day index
        
        (overflowGroup.activities || []).forEach((activity: any) => {
          const alreadyIncluded = mapActivities.some(ma => ma.id === activity.id);
          if (alreadyIncluded) return;
          
          const isSelected = getActivitySelectedStatus(activity.id);
          if (!isSelected) return;
          
          const fullActivity = activitiesLookup.get(activity.id) || activity;
          
          mapActivities.push({
            id: activity.id,
            name: activity.name || fullActivity?.name,
            fullActivity: fullActivity,
            dayIndex: overflowDayIndex, // Use proper dayIndex for color assignment
            isRestaurant: false,
          });
        });
      });
    }
  } else if (isRestaurantsMode) {
    // Show only selected restaurants
    mapActivities = (travelData.restaurants || [])
      .filter((restaurant: any) => {
        const fullRestaurant = restaurantsLookup.get(restaurant.id);
        return fullRestaurant?.isSelected !== false;
      })
      .map((restaurant: any) => ({
        id: restaurant.id,
        name: restaurant.name,
        fullActivity: restaurant,
        dayIndex: -1, // Special marker for restaurants
        isRestaurant: true,
      }));
  } else if (isAllDaysMode) {
    // Show all selected activities from all days with different colors (no restaurants)
    mapActivities = [];
    
    // Include activities from detailed days
    travelData.travel_plan?.forEach((day: any, dayIndex: number) => {
      (day?.activities || []).forEach((activity: any) => {
        const isSelected = getActivitySelectedStatus(activity.id);
        if (isSelected) {
          mapActivities.push({
            id: activity.id,
            name: activity.name,
            fullActivity: activitiesLookup.get(activity.id),
            dayIndex: dayIndex,
            isRestaurant: false,
          });
        }
      });
    });
    
    // Include overflow activities (Days 5-8, etc.) from sections
    if (overflowSection) {
      overflowSection.experiences.forEach((experience) => {
        const isSelected = experience.isSelected !== false;
        if (isSelected) {
          const fullActivity = activitiesLookup.get(experience.id);
          const experienceData = experience.extraDetails as any;
          
          // Calculate proper dayIndex from day_number (0-indexed: day 5 = index 4, day 6 = index 5, etc.)
          const dayNumber = experienceData?.day_number;
          const overflowDayIndex = typeof dayNumber === 'number' && dayNumber > 0 
            ? dayNumber - 1  // Convert 1-based day_number to 0-based dayIndex
            : detailedDayCount; // Fallback to first overflow day index
          
          // Merge activity data similar to single day view
          const mergedActivity = {
            ...(fullActivity || {}),
            ...(experienceData || {}),
            id: experience.id,
            name: experience.name || fullActivity?.name || experienceData?.name,
            description: experience.description || fullActivity?.description || experienceData?.description,
            address: experience.address || fullActivity?.address || experienceData?.address,
            location_name: experienceData?.location_name || fullActivity?.location_name,
            latitude: experienceData?.latitude || fullActivity?.latitude,
            longitude: experienceData?.longitude || fullActivity?.longitude,
            duration: experience.duration || fullActivity?.duration || experienceData?.duration,
            cost: experience.cost || fullActivity?.cost || experienceData?.cost,
            working_hours: experience.workingHours || fullActivity?.working_hours || experienceData?.working_hours,
            category: experience.category || fullActivity?.category || experienceData?.category,
            time_of_visit: experience.timeOfVisit || fullActivity?.time_of_visit || experienceData?.time_of_visit,
          };
          
          mapActivities.push({
            id: experience.id,
            name: experience.name,
            fullActivity: mergedActivity,
            dayIndex: overflowDayIndex, // Use proper dayIndex for color assignment
            isRestaurant: false,
          });
        }
      });
    }
    
    // Also check travelData.overflow_activities directly as a fallback
    // (in case activities aren't in sections yet but are in the raw data)
    if (travelData.overflow_activities && travelData.overflow_activities.length > 0) {
      travelData.overflow_activities.forEach((overflowGroup: any) => {
        const groupDayNumber = overflowGroup.day_number;
        // Calculate proper dayIndex from day_number (0-indexed: day 5 = index 4, day 6 = index 5, etc.)
        const overflowDayIndex = typeof groupDayNumber === 'number' && groupDayNumber > 0 
          ? groupDayNumber - 1  // Convert 1-based day_number to 0-based dayIndex
          : detailedDayCount; // Fallback to first overflow day index
        
        (overflowGroup.activities || []).forEach((activity: any) => {
          // Check if this activity is already included from sections
          const alreadyIncluded = mapActivities.some(ma => ma.id === activity.id);
          if (alreadyIncluded) return;
          
          // In "All Days" mode, include overflow activities (they should be pre-selected)
          // Check if activity is selected (default to true for overflow activities)
          const isSelected = getActivitySelectedStatus(activity.id) !== false;
          if (!isSelected) return;
          
          const fullActivity = activitiesLookup.get(activity.id) || activity;
          
          mapActivities.push({
            id: activity.id,
            name: activity.name || fullActivity?.name,
            fullActivity: fullActivity,
            dayIndex: overflowDayIndex, // Use proper dayIndex for color assignment
            isRestaurant: false,
          });
        });
      });
    }
    
    // Restaurants are not shown on the map in "All Days" mode
  } else {
    // Show only selected activities from the selected day (NO restaurants in single day view)
    // Use the section's experiences directly as they have the most complete data
    // Check if we need to look in overflow section (when activeDayIndex is beyond detailed sections)
    let selectedSection = detailedSections[activeDayIndex];
    
    // If not found in detailed sections, check if it's the overflow section
    if (!selectedSection && activeDayIndex >= detailedSections.length) {
      const overflowSection = sections.find(section => section.id === 'day-overflow');
      if (overflowSection) {
        selectedSection = overflowSection;
      }
    }
    
    if (!selectedSection) {
      mapActivities = [];
    } else {
      // Use experiences from the section - they have the most complete data including addresses
      mapActivities = selectedSection.experiences
        .filter((experience) => {
          // Check if experience is selected
          const isSelected = experience.isSelected !== false;
          if (!isSelected) return false;
          
          return true;
        })
        .map((experience) => {
          // Get full activity details from lookup if available
          const fullActivity = activitiesLookup.get(experience.id);
          
          // Merge section experience data with full activity data
          // Section experience has address, description, etc. from extraDetails
          const experienceData = experience.extraDetails as any;
          
          // Prioritize: fullActivity > experience direct fields > experienceData
          const mergedActivity = {
            ...(fullActivity || {}),
            ...(experienceData || {}),
            // Override with direct experience fields if they exist (most up-to-date)
            id: experience.id,
            name: experience.name || fullActivity?.name || experienceData?.name,
            description: experience.description || fullActivity?.description || experienceData?.description,
            address: experience.address || fullActivity?.address || experienceData?.address,
            location_name: experienceData?.location_name || fullActivity?.location_name,
            latitude: experienceData?.latitude || fullActivity?.latitude,
            longitude: experienceData?.longitude || fullActivity?.longitude,
            duration: experience.duration || fullActivity?.duration || experienceData?.duration,
            cost: experience.cost || fullActivity?.cost || experienceData?.cost,
            working_hours: experience.workingHours || fullActivity?.working_hours || experienceData?.working_hours,
            category: experience.category || fullActivity?.category || experienceData?.category,
            time_of_visit: experience.timeOfVisit || fullActivity?.time_of_visit || experienceData?.time_of_visit,
          };
          
          // Find the day index for this experience
          let actualDayIndex = activeDayIndex;
          if (selectedSection.dayNumber !== undefined) {
            const dayIndex = travelData.travel_plan?.findIndex((d: any, idx: number) => {
              const dDayNumber = typeof d.day_number === 'number' ? d.day_number : idx + 1;
              return dDayNumber === selectedSection.dayNumber;
            }) ?? -1;
            if (dayIndex >= 0) {
              actualDayIndex = dayIndex;
            }
          }
          
          return {
            id: experience.id,
            name: experience.name,
            fullActivity: mergedActivity,
            dayIndex: actualDayIndex,
            isRestaurant: false,
          };
        });
    }
  }

  const baseMapContainerSx = (theme: Theme) => ({
    borderRadius: 4,
    overflow: 'hidden',
    minHeight: { xs: 360, sm: 400, md: 600 },
    height: { xs: 360, sm: 400, md: '75vh' },
    border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
  });

  const getToolbarHeight = (theme: Theme) => {
    const raw = (theme.mixins?.toolbar as any)?.minHeight;
    if (typeof raw === 'number') return raw;
    if (typeof raw === 'string') {
      const parsed = parseInt(raw, 10);
      return Number.isNaN(parsed) ? 64 : parsed;
    }
    return 64;
  };

  const getHeaderOffset = (theme: Theme) => getToolbarHeight(theme) + 16;

  const mergedMapContainerSx: SxProps<Theme> = [
    baseMapContainerSx,
    ...(fullscreen
      ? [
          (theme: Theme) => ({
            position: 'fixed',
            top: getHeaderOffset(theme),
            left: 0,
            right: 0,
            bottom: 0,
            width: '100vw',
            height: `calc(100vh - ${getHeaderOffset(theme)}px)`,
            maxHeight: `calc(100vh - ${getHeaderOffset(theme)}px)`,
            borderRadius: 0,
            border: 'none',
            zIndex: 1350,
            backgroundColor: theme.palette.background.default,
          }),
        ]
      : [
          (theme: Theme) => ({
            mt: 0,
            pt: 0,
          }),
        ]),
    ...(Array.isArray(mapContainerSx)
      ? mapContainerSx
      : mapContainerSx
        ? [mapContainerSx]
        : []),
  ];

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 0,
      }}
    >
      <Box
        sx={(theme) => {
          const headerOffset = getHeaderOffset(theme);
          return {
            position: fullscreen ? 'fixed' : 'sticky',
            top: fullscreen
              ? { xs: 0, sm: headerOffset }
              : { xs: 8, sm: headerOffset + 8 },
            left: fullscreen ? 0 : 'auto',
            right: fullscreen ? 0 : 'auto',
            width: fullscreen ? '100%' : 'auto',
            zIndex: fullscreen ? 1400 : 1200,
            backgroundColor: 'transparent',
            backdropFilter: 'none',
            paddingY: fullscreen ? 1.5 : 1,
            paddingX: fullscreen ? 2 : 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: fullscreen ? 'center' : 'flex-start',
            gap: 1,
            borderRadius: 0,
            boxShadow: 'none',
            mb: fullscreen ? 1 : 0.75,
            pointerEvents: 'none',
            '& > *': {
              pointerEvents: 'auto',
            },
          };
        }}
      >
        {showLeftArrow && (
          <IconButton
            size="small"
            onClick={scrollLeft}
            sx={{
              flexShrink: 0,
              backgroundColor: 'background.paper',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            <ArrowBackIosIcon fontSize="small" />
          </IconButton>
        )}
        <Box
          ref={buttonGroupRef}
          sx={{
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            overflowX: 'auto',
            overflowY: 'visible',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            backgroundColor: 'transparent',
            paddingY: 0.5,
            width: '100%',
            '&::-webkit-scrollbar': {
              display: 'none',
            },
          }}
        >
          <ButtonGroup
            size="small"
            color="primary"
            sx={(theme) => ({
              flexWrap: 'nowrap',
              width: 'fit-content',
              backgroundColor: theme.palette.background.paper,
              borderRadius: 1,
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
              '& .MuiButton-root': {
                whiteSpace: 'nowrap',
                backgroundColor: 'transparent',
              },
              '& .MuiButton-contained': {
                backgroundColor: theme.palette.primary.main,
              },
            })}
          >
            <Button 
              variant={isAllDaysMode ? 'contained' : 'outlined'} 
              onClick={() => onDayChange(-1)}
              sx={{ fontWeight: isAllDaysMode ? 'bold' : 'normal' }}
            >
              All Days
            </Button>
            {detailedSections.map((section, index) => (
              <Button key={section.id} variant={index === activeDayIndex ? 'contained' : 'outlined'} onClick={() => onDayChange(index)}>
                Day {index + 1}
              </Button>
            ))}
            {hasOverflowActivities && (
              <Button 
                variant={isOverflowMode ? 'contained' : 'outlined'} 
                onClick={() => onDayChange(-3)}
                sx={{ fontWeight: isOverflowMode ? 'bold' : 'normal' }}
              >
                {overflowSection?.title || 'More Days'}
              </Button>
            )}
            {hasRestaurants && (
              <Button 
                variant={isRestaurantsMode ? 'contained' : 'outlined'} 
                onClick={() => onDayChange(-2)}
                sx={{ fontWeight: isRestaurantsMode ? 'bold' : 'normal' }}
              >
                Restaurants
              </Button>
            )}
          </ButtonGroup>
        </Box>
        {showRightArrow && (
          <IconButton
            size="small"
            onClick={scrollRight}
            sx={{
              flexShrink: 0,
              backgroundColor: 'background.paper',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            <ArrowForwardIosIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
      <Box
        sx={mergedMapContainerSx}
      >
        <DayMap 
          key={`map-${isRestaurantsMode ? 'restaurants' : isOverflowMode ? 'overflow' : isAllDaysMode ? 'all' : activeDayIndex}`}
          activities={mapActivities} 
          destinationName={travelData.destination} 
          dayNumber={isRestaurantsMode || isOverflowMode || isAllDaysMode ? 0 : (Math.min(activeDayIndex, Math.max(detailedDayCount - 1, 0)) + 1)} 
          onActivitySelect={onActivitySelect}
          showRestaurants={isRestaurantsMode}
          forceFullscreen={fullscreen}
          onFullscreenChange={handleFullscreenChange}
        />
      </Box>
    </Box>
  );
};
